import { CalendarClock } from 'lucide-react';
import { useMyRecoveries, useSchedule } from '../../services/hooks/useClientPortal';

interface Recovery {
  _id: string;
  levelId: { nombre: string } | string;
  rescheduleDeadline: string;
  // B11 — presente si el plazo viene de la recuperación que gastó para entrar
  // en la clase que acaba de cancelar.
  deadlineInheritedFrom?: string | null;
}

const formatDeadline = (d: string) =>
  new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });

const levelName = (level: Recovery['levelId']) =>
  (typeof level === 'object' ? level.nombre : level);

/**
 * Prominent summary of the client's open recoveries ("you have N classes to
 * recover, until DD/MM"). Shown above the calendar; booking the make-up happens
 * by clicking a bookable class on the calendar itself.
 */
const RecoveriesBanner = () => {
  const { data } = useMyRecoveries();
  const recoveries = (data as Recovery[] | undefined) ?? [];
  // P1 — con recuperación en cualquier nivel, nombrar el nivel de la recuperación
  // se lee como "tienes que recuperar ahí", que es justo lo contrario. El
  // calendario ya ha pedido este mismo query, así que TanStack lo sirve de caché.
  const { data: schedule } = useSchedule();
  const mostrarNivel = schedule?.config.recoveryAnyLevel !== true;

  if (recoveries.length === 0) return null;

  const soonest = recoveries.reduce((min, r) =>
    new Date(r.rescheduleDeadline) < new Date(min.rescheduleDeadline) ? r : min
  );

  return (
    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4">
      <CalendarClock size={20} className="shrink-0 mt-0.5" />
      <div className="text-sm">
        {recoveries.length === 1 ? (
          <p>
            Tienes <strong>1 clase</strong> por recuperar
            {mostrarNivel && (
              <>{' '}(<strong className="capitalize">{levelName(recoveries[0].levelId)}</strong>)</>
            )} —{' '}
            hasta el <strong>{formatDeadline(recoveries[0].rescheduleDeadline)}</strong>.
          </p>
        ) : (
          <p>
            Tienes <strong>{recoveries.length} clases</strong> por recuperar —{' '}
            la más próxima hasta el <strong>{formatDeadline(soonest.rescheduleDeadline)}</strong>.
          </p>
        )}
        {/* B11 — una recuperación re-ganada conserva el plazo de la original,
            así que la fecha puede ser anterior a lo que el cliente espera. Sin
            esta línea se lee como un error nuestro. */}
        {soonest.deadlineInheritedFrom && (
          <p className="text-xs text-amber-600 mt-0.5">
            Esa fecha es la de tu recuperación original: cancelar una clase de repuesto no amplía el plazo.
          </p>
        )}
        <p className="text-xs text-amber-600 mt-0.5">Elige abajo una clase para recuperarla.</p>
      </div>
    </div>
  );
};

export default RecoveriesBanner;
